import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // No request interceptor needed for auth any more: the access/refresh
    // tokens live in httpOnly cookies (see docs/architecture.md §6 in
    // spidergolf-nodejs for why), sent automatically by the browser via
    // axios's withCredentials (src/libs/axios.js) -- there is no token for
    // JS to read or attach as an Authorization header.

    // Response Interceptor
    //
    // On a 401 we try the refresh token once, then replay the failed request
    // with the new access token. Only the first 401 triggers a refresh; any
    // other requests that 401 while that refresh is in flight are queued and
    // replayed once it resolves, so a page issuing several calls at once does
    // not fire several refreshes.
    //
    // If there is no refresh token, or the refresh itself fails (the backend's
    // refresh lifetime is the same as its access lifetime, so both can expire
    // together), we clear the session and reject — the router guard then sends
    // the user to the login page.
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        const { config, response } = error
        const originalRequest = config

        if (!response || response.status !== 401) {
          return Promise.reject(error)
        }

        // A 401 from the refresh call itself must not be retried, or we would
        // loop refreshing forever.
        if (originalRequest && originalRequest.url && originalRequest.url.includes(this.jwtConfig.refreshEndpoint)) {
          this.logout()
          return Promise.reject(error)
        }

        // A 401 from login is "wrong credentials", not an expired session:
        // let the login view show its own error instead of clearing storage.
        if (originalRequest && originalRequest.url && originalRequest.url.includes(this.jwtConfig.loginEndpoint)) {
          return Promise.reject(error)
        }

        // Already retried once — the new token was rejected too, so give up
        // rather than looping.
        // eslint-disable-next-line no-underscore-dangle
        if (originalRequest && originalRequest._isRetry) {
          this.logout()
          return Promise.reject(error)
        }

        if (!this.isAlreadyFetchingAccessToken) {
          this.isAlreadyFetchingAccessToken = true

          this.refreshToken()
            .then(() => {
              // The backend rotates refresh tokens on every call and
              // re-sets both cookies via Set-Cookie -- that's what makes
              // the 7-day rolling window actually roll. Nothing for the
              // client to store: the browser already has the new cookies.
              this.isAlreadyFetchingAccessToken = false
              this.onAccessTokenFetched()
            })
            .catch(() => {
              this.isAlreadyFetchingAccessToken = false
              this.onRefreshFailed()
              this.logout()
            })
        }

        return new Promise((resolve, reject) => {
          this.addSubscriber(refreshed => {
            if (!refreshed) {
              reject(error)
              return
            }
            // eslint-disable-next-line no-underscore-dangle, no-param-reassign
            originalRequest._isRetry = true
            resolve(this.axiosIns(originalRequest))
          })
        })
      },
    )
  }

  // Drain the queue of requests that were waiting on a refresh. The queue is
  // emptied before the callbacks run so a callback that itself 401s starts a
  // fresh cycle rather than being replayed against the old queue.
  onAccessTokenFetched() {
    const queued = this.subscribers
    this.subscribers = []
    queued.forEach(callback => callback(true))
  }

  // Refresh failed: release every waiting request so none hang forever.
  onRefreshFailed() {
    const queued = this.subscribers
    this.subscribers = []
    queued.forEach(callback => callback(false))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  // Cookies are httpOnly, so JS can't clear them itself -- this always hits
  // the backend logout endpoint, which does. Best-effort: local state is
  // cleared either way, since the caller is about to redirect to login.
  logout() {
    localStorage.removeItem('userData')
    return this.axiosIns.post(this.jwtConfig.logoutEndpoint).catch(() => {})
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  // No body: the refresh token rides along as a cookie.
  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint)
  }
}
